import React, { Fragment, useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Breadcrumbs } from "../../AbstractElements";
import { Button, Card, CardBody, Col, Row } from "reactstrap";
import { useTranslation } from "react-i18next";
import { getItemDetails, getSaltOnSearch, updateItemInfo } from "./PharmacyMedicineService";
import { useLoader } from "../../_helper/LoaderContext";
import { toast } from "react-toastify";
import { ArrowLeft } from "react-feather";
import { usePharmacy } from "../../_helper/PharmacyProvider";
import AsyncSelect from "react-select/async";
import { useDisableScrollOnFocus } from "../../Hooks/useDisableScrollOnFocus";
import { generateInternalEan13 } from "../../utils/internalEan13";
import BarcodePrintModal from "../Common/Barcode/BarcodePrintModal";
import {
	sanitizeBarcodeInput,
	BARCODE_FIELD_LENGTH,
} from "../../utils/barcodeUtils";
import CreatableOptionSelect from "./components/CreatableOptionSelect";
import {
	appendOptionalItemLists,
	getInteractionsList,
	getPrimaryUseList,
} from "./utils/itemListFields";
import {
	getItemMetaValue,
	getPackageLabel,
	getPackagingDetail,
	getProductForm,
} from "./utils/productMetaFields";
import { shouldShowItemTaxField } from "../PharmacyManageTax/taxConfigUtils";
import "./item-form.scss";

// Product forms (sorted alphabetically)
export const productForms = [
	{ id: 1, name: "Capsule", value: "capsule" },
	{ id: 2, name: "Capsule DR", value: "capsule_dr" },
	{ id: 3, name: "Capsule ER", value: "capsule_er" },
	{ id: 4, name: "Capsule SR", value: "capsule_sr" },
	{ id: 5, name: "Chewable Tablet", value: "chewable_tablet" },
	{ id: 6, name: "Cream", value: "cream" },
	{ id: 7, name: "Drop", value: "drop" },
	{ id: 8, name: "Dusting Powder", value: "dusting_powder" },
	{ id: 9, name: "Ear Drop", value: "ear_drop" },
	{ id: 10, name: "Effervescent Tablet", value: "effervescent_tablet" },
	{ id: 11, name: "Expectorant", value: "expectorant" },
	{ id: 12, name: "Gel", value: "gel" },
	{ id: 13, name: "Granule", value: "granule" },
	{ id: 14, name: "Infusion", value: "infusion" },
	{ id: 15, name: "Injection", value: "injection" },
	{ id: 16, name: "Kit", value: "kit" },
	{ id: 17, name: "Liquid", value: "liquid" },
	{ id: 18, name: "Lozenge", value: "lozenge" },
	{ id: 19, name: "Lotion", value: "lotion" },
	{ id: 20, name: "Nail Lacquer", value: "nail_lacquer" },
	{ id: 21, name: "Nasal Spray", value: "nasal_spray" },
	{ id: 22, name: "Ointment", value: "ointment" },
	{ id: 23, name: "Ophthalmic Solution", value: "ophthalmic_solution" },
	{ id: 24, name: "Oral Drop", value: "oral_drop" },
	{ id: 25, name: "Oral Gel", value: "oral_gel" },
	{ id: 26, name: "Oral Solution", value: "oral_solution" },
	{ id: 27, name: "Oral Suspension", value: "oral_suspension" },
	{ id: 28, name: "Paste", value: "paste" },
	{ id: 29, name: "Powder", value: "powder" },
	{ id: 30, name: "Powder for Infusion", value: "powder_for_infusion" },
	{ id: 31, name: "Powder for Injection", value: "powder_for_injection" },
	{ id: 32, name: "Powder for Oral Suspension", value: "powder_for_oral_suspension" },
	{ id: 33, name: "Rediuse Suspension", value: "rediuse_suspension" },
	{ id: 34, name: "Respule", value: "respule" },
	{ id: 35, name: "Solution", value: "solution" },
	{ id: 36, name: "Solution for Infusion", value: "solution_for_infusion" },
	{ id: 37, name: "Solution for Injection", value: "solution_for_injection" },
	{ id: 38, name: "Soap", value: "soap" },
	{ id: 39, name: "Spray", value: "spray" },
	{ id: 40, name: "Soft Gelatin Capsule", value: "soft_gelatin_capsule" },
	{ id: 41, name: "Strip", value: "strip" },
	{ id: 42, name: "Sublingual Tablet", value: "sublingual_tablet" },
	{ id: 43, name: "Suspension", value: "suspension" },
	{ id: 44, name: "Tablet", value: "tablet" },
	{ id: 45, name: "Tablet CR", value: "tablet_cr" },
	{ id: 46, name: "Tablet DT", value: "tablet_dt" },
	{ id: 47, name: "Tablet ER", value: "tablet_er" },
	{ id: 48, name: "Tablet FC", value: "tablet_fc" },
	{ id: 49, name: "Tablet MD", value: "tablet_md" },
	{ id: 50, name: "Tablet MR", value: "tablet_mr" },
	{ id: 51, name: "Tablet PR", value: "tablet_pr" },
	{ id: 52, name: "Tablet SR", value: "tablet_sr" },
	{ id: 53, name: "Tablet XL", value: "tablet_xl" },
	{ id: 54, name: "Tablet Combikit", value: "tablet_combikit" },
	{ id: 55, name: "Tablet Combipack", value: "tablet_combipack" },
	{ id: 56, name: "Dry Syrup", value: "dry_syrup" },
	{ id: 57, name: "Eye Drop", value: "eye_drop" },
	{ id: 58, name: "Eye Ointment", value: "eye_ointment" },
	{ id: 59, name: "Eye/Ear Drop", value: "eye_ear_drop" },
	{ id: 60, name: "Pastille", value: "pastille" },
	{ id: 61, name: "Other", value: "other" }
]

// Package types (sorted alphabetically)
export const packageTypes = [
	{ id: 1, name: "Ampoule", value: "ampoule" },
	{ id: 2, name: "Box", value: "box" },
	{ id: 3, name: "Bottle", value: "bottle" },
	{ id: 4, name: "Kit", value: "kit" },
	{ id: 5, name: "Packet", value: "packet" },
	{ id: 6, name: "Prefilled Syringe", value: "prefilled_syringe" },
	{ id: 7, name: "Pump Bottle", value: "pump_bottle" },
	{ id: 8, name: "Sachet", value: "sachet" },
	{ id: 9, name: "Strip", value: "strip" },
	{ id: 10, name: "Tube", value: "tube" },
	{ id: 11, name: "Vial", value: "vial" },
	{ id: 12, name: "Tablet", value: "tablet" },
	{ id: 13, name: "Other", value: "other" }
]


// OTC product forms (sorted alphabetically)
export const otcProductForms = [
	{ id: 1, name: "Balm / Stick", value: "balm" },
	{ id: 2, name: "Biscuits / Snacks", value: "snack" },
	{ id: 3, name: "Capsule", value: "capsule" },
	{ id: 4, name: "Chewable Tablet", value: "chewable_tablet" },
	{ id: 5, name: "Cream", value: "cream" },
	{ id: 6, name: "Drops", value: "drops" },
	{ id: 7, name: "Drinks / Beverages", value: "drink" },
	{ id: 8, name: "Effervescent Tablet", value: "effervescent_tablet" },
	{ id: 9, name: "Film / Oral Film", value: "oral_film" },
	{ id: 10, name: "Gel", value: "gel" },
	{ id: 11, name: "Gummy / Chewable Bar", value: "gummy" },
	{ id: 12, name: "Lotion", value: "lotion" },
	{ id: 13, name: "Lozenge / Troche", value: "lozenge" },
	{ id: 14, name: "Medicated Soap / Bar", value: "medicated_soap" },
	{ id: 15, name: "Nebulizer Solution", value: "nebulizer_solution" },
	{ id: 16, name: "Patch", value: "patch" },
	{ id: 17, name: "Powder", value: "powder" },
	{ id: 18, name: "Powder for Infusion / Injection (reconstitution)", value: "powder_for_infusion" },
	{ id: 19, name: "Powder for Suspension", value: "powder_for_suspension" },
	{ id: 20, name: "Syrup / Oral Solution", value: "syrup" },
	{ id: 21, name: "Soft Gelatin Capsule", value: "soft_gelatin_capsule" },
	{ id: 22, name: "Spray / Inhaler", value: "spray" },
	{ id: 23, name: "Suppository", value: "suppository" },
	{ id: 24, name: "Tablet", value: "tablet" },
	{ id: 25, name: "Other", value: "other" }
]

// OTC package types (sorted alphabetically)
export const otcPackageTypes = [
	{ id: 1, name: "Ampoule / Vial", value: "ampoule" },
	{ id: 2, name: "Blister Pack", value: "blister_pack" },
	{ id: 3, name: "Bottle", value: "bottle" },
	{ id: 4, name: "Box / Carton", value: "box" },
	{ id: 5, name: "Dropper + Bottle Combo", value: "dropper_combo" },
	{ id: 6, name: "Dropper Bottle", value: "dropper_bottle" },
	{ id: 7, name: "Foil Pack / Foil Sachet", value: "foil_pack" },
	{ id: 8, name: "Jar", value: "jar" },
	{ id: 9, name: "Jar with Screw Cap", value: "jar_screw_cap" },
	{ id: 10, name: "Pump Bottle", value: "pump_bottle" },
	{ id: 11, name: "Package", value: "package" },
	{ id: 12, name: "Roll-on / Pen Dispenser", value: "roll_on" },
	{ id: 13, name: "Sachet / Pouch", value: "sachet" },
	{ id: 14, name: "Spray Bottle / Aerosol", value: "spray_bottle" },
	{ id: 15, name: "Stick Pack / Stick Tube", value: "stick_pack" },
	{ id: 16, name: "Tube", value: "tube" },
	{ id: 17, name: "Tube with Nozzle", value: "tube_nozzle" },
	{ id: 18, name: "Other", value: "other" }
]

const UpdateMedicine = () => {
	const { mdcId } = useParams();
	const { t } = useTranslation();
	const history = useNavigate();
	const { setLoading } = useLoader();
	const { pharmacyPOS } = usePharmacy();

	const [formData, setFormData] = useState({
		itemType: "medicine", // medicine | otc | unani
		brandName: "",
		saltName: "",
		saltId: "",
		manufacturer: "",
		package: "",
		productForm: "",
		quantity: "",
		regularPrice: "",
		sellingPrice: "",
		barcode: "",
		note: "",
		minAlertQuantity: "",
		currency: "",
		tax: "",
		primaryUse: [],
		interactions: [],
		packaging_detail: "",
		metaValue: "",
	});

	const isOTC = formData.itemType === "otc";
	const isUnani = formData.itemType === "unani";
	const isMedicineLike = formData.itemType === "medicine" || formData.itemType === "unani";
	const showTaxField = shouldShowItemTaxField(pharmacyPOS, formData.itemType);

	const disableScrollProps = useDisableScrollOnFocus();
	const [errors, setErrors] = useState({});
	const [showBarcodePrint, setShowBarcodePrint] = useState(false);
	const [selectedOption, setSelectedOption] = useState(null);
	const [searchQuery, setSearchQuery] = useState("");

	const createItemBarcode = useCallback(
		() => generateInternalEan13(pharmacyPOS?.hospital_id),
		[pharmacyPOS?.hospital_id]
	);

	const resolveBarcode = (existing) => {
		const trimmed = String(existing || "").trim();
		if (trimmed) return trimmed;
		return createItemBarcode();
	};

	const resolveItemType = (item) => {
		if (item?.isOTC === true || item?.isOTC === "true") return "otc";
		if (item?.isUnani === true || item?.isUnani === "true") return "unani";
		return "medicine";
	};

	// ✅ Fetch item details on load
	useEffect(() => {
		const fetchItem = async () => {
			try {
				setLoading(true);
				const body = {
					itemId: mdcId,
					clinicId: pharmacyPOS?.hospital_id,
				}

				const res = await getItemDetails(body);

				if (res?.data?.data) {
					const item = res.data.data;

					setFormData({
						itemType: resolveItemType(item),
						brandName: item.brandName || "",
						saltName: item.saltName || "",
						manufacturer: item.manufacturer || "",
						package: item.package || "",
						productForm: item.productForm || "",
						quantity: item.packageQuantity || "",
						regularPrice: item.regularPrice || "",
						barcode: resolveBarcode(item.barcode),
						note: item.note || "",
						minAlertQuantity: item.minQuantityAlert || "",
						currency: item.currency || "INR",
						tax: item.tax || "",
						primaryUse: getPrimaryUseList(item),
						interactions: getInteractionsList(item),
						packaging_detail: getPackagingDetail(item),
						metaValue: getItemMetaValue(item),
					});
				}
			} catch (error) {
				console.error("Error fetching item:", error);
			} finally {
				setLoading(false);
			}
		};

		fetchItem();
	}, [mdcId, pharmacyPOS?.hospital_id, setLoading]);

	// ✅ Load medicines on search
	const loadMedicines = async (inputValue) => {
		if (!inputValue || inputValue.length < 3) {
			return [];
		}

		const body = {
			search: inputValue,
			type: isOTC ? "otc" : "",
		};

		try {
			setLoading(true);
			const response = await getSaltOnSearch(body);
			const medicines = response.data.data || [];

			return medicines.map((m) => {
				if (isOTC) {
					return {
						label: m.name || m.salt_composition || "",
						value: m._id,
						raw: m,
					};
				}
				return {
					label: m.name
						? `${m.name} [${m.salt_composition || ""}]`
						: m.salt_composition || m.salt_composition_new || "",
					value: m._id,
					raw: m,
				};
			});
		} catch (error) {
			console.error("Error fetching medicines:", error);
			return [];
		} finally {
			setLoading(false);
		}
	};

	const handleMedicineSearchChange = (newValue) => {
		setSearchQuery(newValue);
	};

	// ✅ When a medicine/OTC is selected
	const handleSelectMedicine = (option) => {
		setSelectedOption(option);

		if (!option) {
			return;
		}

		const medicine = option.raw || {};
		const newBarcode = createItemBarcode();

		setFormData((prev) => ({
			...prev,
			brandName: medicine.name || prev.brandName,
			saltName: medicine.salt_composition || medicine.salt_composition_new || prev.saltName,
			saltId: medicine._id || prev.saltId,
			manufacturer: medicine.manufacturer || prev.manufacturer,
			package: getPackageLabel(medicine) || prev.package,
			productForm: getProductForm(medicine) || prev.productForm,
			regularPrice: medicine.mrp || prev.regularPrice,
			quantity: medicine.package_qty || medicine.quantity || prev.quantity,
			...(shouldShowItemTaxField(pharmacyPOS, prev.itemType)
				? { tax: medicine.tax || prev.tax }
				: {}),
			barcode: newBarcode,
			primaryUse: getPrimaryUseList(medicine).length
				? getPrimaryUseList(medicine)
				: prev.primaryUse,
			interactions: getInteractionsList(medicine).length
				? getInteractionsList(medicine)
				: prev.interactions,
			packaging_detail: getPackagingDetail(medicine) || prev.packaging_detail,
			metaValue: getItemMetaValue(medicine) || prev.metaValue,
		}));
	};

	const handleRegenerateBarcode = () => {
		setFormData((prev) => ({ ...prev, barcode: createItemBarcode() }));
		setErrors((prev) => ({ ...prev, barcode: "" }));
	};

	// ✅ Handle input change
	const handleChange = (e) => {
		const { name, value } = e.target;
		if (name === "barcode") {
			setFormData((prev) => ({
				...prev,
				barcode: sanitizeBarcodeInput(value),
			}));
			if (errors.barcode) {
				setErrors((prev) => ({ ...prev, barcode: "" }));
			}
			return;
		}
		setFormData((prev) => ({ ...prev, [name]: value }));
		if (errors[name]) {
			setErrors((prev) => ({ ...prev, [name]: "" }));
		}
	};

	// ✅ Validation — brand/product name always; salt only for Medicine / Unani
	const validate = () => {
		const newErrors = {};

		if (!formData.brandName?.trim()) {
			newErrors.brandName = isOTC
				? t("Product name is required")
				: t("Brand name is required");
		}
		if (isMedicineLike && !formData.saltName?.trim()) {
			newErrors.saltName = t("Salt name / composition is required");
		}

		return newErrors;
	};

	// ✅ Submit update
	const handleSubmit = async (e) => {
		e.preventDefault();
		const validationErrors = validate();
		if (Object.keys(validationErrors).length > 0) {
			setErrors(validationErrors);
			toast.warning(t("Please fill in all required fields"));
			return;
		}
		try {
			setLoading(true);

			const body = {
				itemId: mdcId,
				clinicId: pharmacyPOS?.hospital_id,
				isOTC,
				isUnani,
				brandName: formData.brandName,
				saltName: formData.saltName,
				medicationId: formData.saltId,
				manufacturer: formData.manufacturer,
				package: formData.package,
				productForm: formData.productForm,
				packageQuantity: formData.quantity,
				regularPrice: formData.regularPrice,
				barcode: formData.barcode,
				note: formData.note,
				minQuantityAlert: formData.minAlertQuantity,
				currency: formData.currency,
				...(showTaxField ? { tax: formData.tax } : {}),
			};

			appendOptionalItemLists(body, {
				primary_use: formData.primaryUse,
				interactions: formData.interactions,
				packaging_detail: formData.packaging_detail,
				value: formData.metaValue,
			});
			const response = await updateItemInfo(body)
			toast.success(response.data.message);
			gotoList();
		} catch (error) {
			console.error("Error updating item:", error);
		} finally {
			setLoading(false);
		}
	};

	const gotoList = () => {
		history(`${process.env.PUBLIC_URL}/items`);
	}
	const isDark = document.body.classList.contains("dark-only");

	const getSearchPlaceholder = () => {
		if (isOTC) return t("Search OTC products by name...");
		if (isUnani) return t("Search Unani medicines by name or salt composition...");
		return t("Search medicines by name or salt composition...");
	};

	return (
		<Fragment>
			<Breadcrumbs mainTitle={t("Update Item")} parent={t("Dashboard")} title={t("Update Item")} />
			<Row className="mt-2 item-form-page">
				<Col sm={12}>
					<Card className="item-form-card">
						<CardBody className="item-form-body">
							<form onSubmit={handleSubmit} className="item-form">
								<div className="item-form-toolbar">
									<div className="item-form-type">
										<label className="form-label">{t("Item Type")}</label>
										<div className="item-type-switch">
											<label className="item-type-option">
												<input type="radio" name="itemType" value="medicine" checked={formData.itemType === "medicine"} disabled readOnly />
												<span title={t("Medicine")}>{t("Medicine")}</span>
											</label>
											<label className="item-type-option">
												<input type="radio" name="itemType" value="unani" checked={formData.itemType === "unani"} disabled readOnly />
												<span title={t("Unani Medicine")}>{t("U Medicine")}</span>
											</label>
											<label className="item-type-option">
												<input type="radio" name="itemType" value="otc" checked={formData.itemType === "otc"} disabled readOnly />
												<span title="OTC">OTC</span>
											</label>
										</div>
									</div>

									<div className="item-form-search">
										<label className="form-label">
											{isOTC ? t("Search Product") : isUnani ? t("Search U Medicine") : t("Search Medicine")}
										</label>
										<AsyncSelect
											cacheOptions
											loadOptions={loadMedicines}
											defaultOptions={[]}
											onChange={handleSelectMedicine}
											onInputChange={handleMedicineSearchChange}
											placeholder={getSearchPlaceholder()}
											noOptionsMessage={() =>
												searchQuery.length >= 3
													? t("No item found. Try another name.")
													: t("Type at least 3 characters to search...")
											}
											isClearable
											value={selectedOption}
											styles={{
												control: (base) => ({
													...base,
													minHeight: "36px",
													fontSize: "0.875rem",
													borderRadius: "8px",
													borderColor: "#d0d5dd",
												}),
											}}
											theme={(theme) => ({
												...theme,
												colors: {
													...theme.colors,
													primary25: isDark ? "#373737" : "#e6f7ffff",
												},
											})}
											key={formData.itemType}
										/>
									</div>

									<div className="item-form-toolbar-actions">
										{formData.barcode && (
											<Button color="primary" outline size="sm" className="item-form-back-btn" onClick={() => setShowBarcodePrint(true)}>
												{t("Print")}
											</Button>
										)}
										<button type="button" className="btn btn-primary d-flex align-items-center item-form-back-btn" onClick={gotoList}>
											<ArrowLeft size={15} className="me-1" /> {t("Back")}
										</button>
									</div>
								</div>

								<div className="item-form-section">
									<div className="item-form-section-title">{t("Product Details")}</div>
									<div className="row g-2">
										<div className={isOTC ? "col-lg-7 col-md-8" : "col-lg-5 col-md-6"}>
											<label className="form-label">
												{isOTC ? t("Product Name") : t("Brand Name")}
												<span className="required-mark">*</span>
											</label>
											<input
												type="text"
												name="brandName"
												value={formData.brandName}
												onChange={handleChange}
												className={`form-control ${errors.brandName ? "is-invalid" : ""}`}
												placeholder={isOTC ? t("Enter product name") : t("Enter brand name")}
											/>
											{errors.brandName && <div className="text-danger field-error">{errors.brandName}</div>}
										</div>
										{isMedicineLike && (
											<div className="col-lg-5 col-md-6">
												<label className="form-label">
													{t("Salt Name / Composition")}
													<span className="required-mark">*</span>
												</label>
												<input
													type="text"
													name="saltName"
													value={formData.saltName}
													onChange={handleChange}
													className={`form-control ${errors.saltName ? "is-invalid" : ""}`}
													placeholder={t("Enter salt name or composition")}
												/>
												{errors.saltName && <div className="text-danger field-error">{errors.saltName}</div>}
											</div>
										)}
										<div className={isOTC ? "col-lg-5 col-md-4" : "col-lg-2 col-md-12"}>
											<label className="form-label">{t("Manufacturer")}</label>
											<input type="text" name="manufacturer" value={formData.manufacturer} onChange={handleChange} className="form-control" placeholder={t("Manufacturer")} />
										</div>
									</div>
								</div>

								<hr className="item-form-divider" />

								<div className="item-form-section">
									<div className="item-form-section-title">{t("Pack & Pricing")}</div>
									<div className="row g-2">
										<div className="col-lg-3 col-md-4 col-6">
											<label className="form-label">{t("Packaging")}</label>
											<CreatableOptionSelect
												options={isOTC ? otcPackageTypes : packageTypes}
												value={formData.package}
												onChange={(value) =>
													setFormData((prev) => ({ ...prev, package: value }))
												}
												placeholder={t("Search or type packaging")}
											/>
										</div>
										<div className="col-lg-3 col-md-4 col-6">
											<label className="form-label">{t("Form")}</label>
											<CreatableOptionSelect
												options={isOTC ? otcProductForms : productForms}
												value={formData.productForm}
												onChange={(value) =>
													setFormData((prev) => ({ ...prev, productForm: value }))
												}
												placeholder={t("Search or type form")}
											/>
										</div>
										<div className="col-lg-2 col-md-4 col-6">
											<label className="form-label">{t("Units / Pack")}</label>
											<input type="number" name="quantity" min={1} value={formData.quantity} onChange={handleChange} className="form-control" {...disableScrollProps} />
										</div>
										<div className="col-lg-2 col-md-4 col-6">
											<label className="form-label">{t("Pack MRP")}</label>
											<input type="number" name="regularPrice" value={formData.regularPrice} onChange={handleChange} className="form-control" {...disableScrollProps} />
										</div>
										{showTaxField && (
											<div className="col-lg-1 col-md-4 col-6">
												<label className="form-label">{t("Tax %")}</label>
												<input type="number" name="tax" min={0} value={formData.tax} onChange={handleChange} className="form-control" {...disableScrollProps} placeholder="0" />
											</div>
										)}
										<div className="col-lg-1 col-md-4 col-6">
											<label className="form-label">{t("Currency")}</label>
											<select name="currency" value={formData.currency} onChange={handleChange} className="form-select" disabled>
												<option value="INR">INR (₹)</option>
											</select>
										</div>
									</div>
								</div>

								<div className="item-form-section mb-0">
									<div className="row g-2">
										<div className="col-lg-3 col-md-4">
											<label className="form-label">{t("Min Stock Alert")}</label>
											<input type="number" name="minAlertQuantity" min={0} value={formData.minAlertQuantity} onChange={handleChange} className="form-control" {...disableScrollProps} />
										</div>
										<div className="col-lg-4 col-md-4">
											<label className="form-label">{t("Bar Code")}</label>
											<div className="input-group">
												<input
													type="text"
													name="barcode"
													value={formData.barcode}
													onChange={handleChange}
													className="form-control"
													placeholder={t("13-digit barcode")}
													maxLength={BARCODE_FIELD_LENGTH}
													inputMode="numeric"
													autoComplete="off"
													{...disableScrollProps}
												/>
												<button type="button" className="btn btn-outline-secondary z-0" onClick={handleRegenerateBarcode} title={t("Generate barcode")}>
													<i className="fa fa-refresh" aria-hidden="true" />
												</button>
											</div>
										</div>
										<div className="col-lg-5 col-md-4">
											<label className="form-label">{t("Notes")}</label>
											<input type="text" name="note" value={formData.note} onChange={handleChange} className="form-control" placeholder={t("Optional notes")} />
										</div>
									</div>
								</div>

								<div className="item-form-actions">
									<button className="btn btn-primary" type="submit">{t("Update Item")}</button>
									<Button color="secondary" onClick={gotoList}>{t("Cancel")}</Button>
								</div>
							</form>
						</CardBody>
					</Card>
				</Col>
			</Row>

			<BarcodePrintModal
				show={showBarcodePrint}
				onHide={() => setShowBarcodePrint(false)}
				product={{
					barcode: formData.barcode,
					brandName: formData.brandName,
					saltName: formData.saltName,
					regularPrice: formData.regularPrice,
					currency: formData.currency,
				}}
			/>
		</Fragment>
	);
};

export default UpdateMedicine;
